import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { type UserIdRow } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableId, CopyableEmail } from "../components/CopyableId";

type Report = {
  bullclipOnly: UserIdRow[]; // Drawboard Projects profile, no identity — can't sign in
  identityOnly: UserIdRow[]; // identity, no Drawboard Projects profile — no app profile/org
  projectsTotal: number;
  identityTotal: number;
  systemUsersExcluded: number;
};

// Purpose-made users that intentionally exist on only one side of the reconciliation — they'd
// show up as false positives here:
//  - @apikeys.drawboard.com — backing users for API keys (Projects profile, no identity)
//  - @example.com           — demo-project sample users (Projects profile, no identity)
//  - <userId>@drawboard.com — deleted users: the profile is kept (anonymized) but the identity is
//    deleted, so they'd otherwise all report as broken. Exact-match only — staff have real
//    @drawboard.com addresses.
const EXCLUDED_EMAIL_SUFFIXES = ["@apikeys.drawboard.com", "@example.com"];
const isSystemUser = (u: UserIdRow) => {
  const email = (u.email ?? "").toLowerCase();
  return EXCLUDED_EMAIL_SUFFIXES.some((suffix) => email.endsWith(suffix))
    || email === `${u.userId.toLowerCase()}@drawboard.com`;
};

/**
 * Reconciles the two systems that share a user id — Drawboard Projects (Bullclip backend) and the
 * identity server. A user present in only one is broken. The id lists are pulled from each backend
 * and diffed client-side; mismatches are rare so the payload is light.
 */
export function BrokenUsersPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "users", label: "Users", active: false, onSelect: () => navigate("/support/users") },
    { id: "guests", label: "Guest users", active: false, onSelect: () => navigate("/support/guest-users") },
    { id: "broken", label: "Broken users", active: true },
  ]);

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const [projects, identity] = await Promise.all([
        api.getBullclipUserIds(),
        api.getIdentityUserIds(),
      ]);
      const identityIds = new Set(identity.map((u) => u.userId));
      const projectIds = new Set(projects.map((u) => u.userId));
      const bullclipOrphans = projects.filter((u) => !identityIds.has(u.userId));
      const identityOrphans = identity.filter((u) => !projectIds.has(u.userId));
      const bullclipOnly = bullclipOrphans.filter((u) => !isSystemUser(u));
      const identityOnly = identityOrphans.filter((u) => !isSystemUser(u));
      setReport({
        bullclipOnly,
        identityOnly,
        projectsTotal: projects.length,
        identityTotal: identity.length,
        systemUsersExcluded:
          bullclipOrphans.length - bullclipOnly.length + (identityOrphans.length - identityOnly.length),
      });
    } catch (e) {
      setError(`${e}`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void run(); }, [run]);

  function openUser(u: UserIdRow) {
    const p = new URLSearchParams();
    p.set("user", u.userId);
    if (u.email) p.set("email", u.email);
    navigate(`/support/users?${p.toString()}`);
  }

  const clean = report != null && report.bullclipOnly.length === 0 && report.identityOnly.length === 0;

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Users · Broken users</div>
        <h1>Broken users</h1>
        <p>
          Reconciles Drawboard Projects and the identity server, which share a user id. A user present
          in only one system is broken — no identity record means they can't sign in; no Drawboard
          Projects profile means they have no app profile or organization. Purpose-made system users
          are excluded: <span className="mono">@apikeys.drawboard.com</span> (API keys) and{" "}
          <span className="mono">@example.com</span> (demo-project sample users).
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Reconciliation
          <span className="hint">
            {report
              ? `${report.projectsTotal} Projects · ${report.identityTotal} identity users` +
                (report.systemUsersExcluded > 0 ? ` · ${report.systemUsersExcluded} system users excluded` : "")
              : "compares all user ids in both systems"}
          </span>
          <button
            className={"btn btn-ghost btn-sm" + (loading ? " loading" : "")}
            style={{ marginLeft: "auto" }}
            disabled={loading}
            onClick={() => void run()}
          >
            Re-run
          </button>
        </header>
        <div className="body">
          {error && (
            <div className="callout error"><span className="mk">err</span><span>{error}</span></div>
          )}
          {loading && !report && <div className="hint">reconciling…</div>}
          {clean && (
            <div className="callout ok">
              <span className="mk">ok</span>
              <span>No mismatches found — every user exists in both systems.</span>
            </div>
          )}
          {report && !clean && (
            <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
              {report.bullclipOnly.length > 0 && (
                <OrphanTable
                  title={`No identity record · ${report.bullclipOnly.length}`}
                  subtitle="Has a Drawboard Projects profile, but no identity-server account — cannot sign in."
                  rows={report.bullclipOnly}
                  onOpen={openUser}
                />
              )}
              {report.identityOnly.length > 0 && (
                <OrphanTable
                  title={`No Drawboard Projects profile · ${report.identityOnly.length}`}
                  subtitle="Has an identity-server account, but no Drawboard Projects profile / organization."
                  rows={report.identityOnly}
                  onOpen={openUser}
                />
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function OrphanTable({ title, subtitle, rows, onOpen }: {
  title: string;
  subtitle: string;
  rows: UserIdRow[];
  onOpen: (u: UserIdRow) => void;
}) {
  return (
    <div>
      <div className="banner-broken" style={{ marginBottom: 0 }}>
        <div className="banner-broken-title">{title}</div>
        <div className="banner-broken-body">{subtitle}</div>
      </div>
      <div style={{ border: "1px solid var(--red)", borderTop: "none" }}>
        <table className="grid">
          <thead>
            <tr>
              <th>Email</th>
              <th>User id</th>
              <th className="actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.userId} style={{ cursor: "pointer" }} onClick={() => onOpen(u)}>
                <td><CopyableEmail email={u.email} /></td>
                <td><CopyableId value={u.userId} /></td>
                <td className="actions">
                  <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); onOpen(u); }}>
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
